import React from 'react'
import type { Block, Variable } from '../../editor/types'

interface ToggleBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
  onSetVariableValue: (name: string, value: number | string | boolean) => void
}

import { Check } from 'lucide-react'

type ToggleStyle = 'switch' | 'pill' | 'checkbox' | 'segmented'

export default function ToggleBlock({ block, variableValues, variables, onSetVariableValue }: ToggleBlockProps) {
  const varName = block.variable || ''
  const variable = variables.find((v) => v.name === varName)
  const isOn = Boolean(variableValues[varName] ?? variable?.default ?? false)
  const config = (block.config || {}) as Record<string, unknown>
  const onLabel = (config.onLabel as string) || 'ON'
  const offLabel = (config.offLabel as string) || 'OFF'
  const style = (config.variant as ToggleStyle) || 'switch'
  const accent = block.accent || 'var(--accent, #5048e5)'
  const toggle = () => onSetVariableValue(varName, !isOn)

  return (
    <div className="flex items-center justify-between gap-4">
      {block.label && (
        <label className="text-sm font-semibold text-slate-900">{block.label}</label>
      )}

      {style === 'switch' && (
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold uppercase tracking-wider ${isOn ? 'text-slate-300' : 'text-slate-600'}`}>
            {offLabel}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={isOn}
            onClick={toggle}
            className="relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ backgroundColor: isOn ? accent : '#cbd5e1' }}
          >
            <span
              className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out"
              style={{
                transform: isOn ? 'translate(22px, 4px)' : 'translate(4px, 4px)',
              }}
            />
          </button>
          <span className={`text-xs font-bold uppercase tracking-wider ${isOn ? 'text-slate-600' : 'text-slate-300'}`}>
            {onLabel}
          </span>
        </div>
      )}

      {style === 'pill' && (
        <button
          onClick={toggle}
          className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
          style={{
            backgroundColor: isOn ? accent : '#f1f5f9',
            color: isOn ? 'white' : '#64748b',
          }}
        >
          {isOn ? onLabel : offLabel}
        </button>
      )}

      {style === 'checkbox' && (
        <button
          onClick={toggle}
          className="flex items-center gap-2.5 cursor-pointer group"
        >
          <div
            className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
            style={{
              backgroundColor: isOn ? accent : 'white',
              borderColor: isOn ? accent : '#cbd5e1',
            }}
          >
            {isOn && <Check className="h-3.5 w-3.5 text-white" />}
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
            {isOn ? onLabel : offLabel}
          </span>
        </button>
      )}

      {style === 'segmented' && (
        <div className="flex rounded-lg overflow-hidden border border-slate-200">
          <button
            onClick={() => isOn && toggle()}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
            style={{
              backgroundColor: !isOn ? accent : 'white',
              color: !isOn ? 'white' : '#64748b',
            }}
          >
            {offLabel}
          </button>
          <button
            onClick={() => !isOn && toggle()}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
            style={{
              backgroundColor: isOn ? accent : 'white',
              color: isOn ? 'white' : '#64748b',
            }}
          >
            {onLabel}
          </button>
        </div>
      )}
    </div>
  )
}
